import axios from 'axios';
import { ref, computed, watch } from 'vue';
import baseConfig from '@config/base.config';
import { cloneDeep } from 'lodash-es';
import { langMap } from '@experience/static/config/experience.config';
import { useAccountInfo } from '@models/account/useAccountInfo';

export type ECTemplateTags = {
    industry: string[];
    language: 'English' | 'Japanese' | 'Chinese';
    layout: 'instruction' | 'detail' | 'card';
    location: 'Blank page' | 'Create Experience popup' | 'Home page' | 'Create Popup Dialog';
    priority: string;
    purpose: string[];
    test: boolean;
    type: 'Web Editing' | 'Engagement';
};
export type ECTemplate = {
    appId: string;
    id: string;
    name: string;
    i18nName?: string;
    i18nLink1?: string;
    i18nLink2?: string;
    i18nDesc?: string;
    publishedAt: number;
    type: 'COMPONENT';
    isfromEC?: boolean;
    tags?: ECTemplateTags;
    data?: any;
    updated?: number;
    updateTime?: number;
};

let allECTemplates = ref();
let ecTemplateLoading = ref(false);
export async function findEcAssetByAppId(appId: string) {
    const api = import.meta.env.VITE_EC_API_URL?.toString() + '/asset/find';
    const authorization = `Bearer ${import.meta.env.VITE_EC_API_TOKEN}`;
    const res = await axios.post(
        api,
        {
            type: 'COMPONENT',
            appId
        },
        {
            headers: {
                'Content-Type': 'application/json',
                authorization
            }
        }
    );
    return res?.data;
}

export function useECTemplates() {
    const { account } = useAccountInfo();
    const currentArea = computed(() => {
        return /(ptmind|ptengine)\.com/.test(location.host) ? 'en' : 'jp';
    });
    const language = computed(() => {
        return account.value ? account.value?.language?.toLocaleLowerCase() : currentArea.value;
    });
    const wacLang = computed(() => {
        return langMap[language.value];
    });
    const getECTemplates = async function () {
        ecTemplateLoading.value = true;
        const api = import.meta.env.VITE_EC_API_URL?.toString() + '/asset/find';
        const authorization = `Bearer ${import.meta.env.VITE_EC_API_TOKEN}`;
        const appId = import.meta.env.VITE_EC_APP_ID;
        const res = await axios.post(
            api,
            {
                type: 'COMPONENT',
                appId,
                includeData: true
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    authorization
                }
            }
        );
        allECTemplates.value = fromatECTemplate(res?.data || [], langMap[language.value]) || [];
        ecTemplateLoading.value = false;
    };

    return {
        wacLang,
        allECTemplates,
        ecTemplateLoading,
        getECTemplates
    };
}

export function fromatECTemplate(list: ECTemplate[], language: string) {
    const { account } = useAccountInfo();
    //res?.data || []
    return (
        cloneDeep(list)?.map((item: ECTemplate) => {
            const data = item.data?.data;
            if (!data) return;
            let dataConfig, attrConfig, linkConfig;
            let area = account.value?.area;
            if (!area) {
                const isEN = window.location.host.toLocaleLowerCase().includes('.com');
                if (!isEN) {
                    area = 'JP';
                } else {
                    area = 'EN';
                }
            }
            if (Array.isArray(data)) {
                dataConfig = data[0]?.config?.data?.data?.[language];
                attrConfig = data[0]?.config?.attrs?.data;
                linkConfig =
                    area === 'JP'
                        ? data[0]?.config?.data?.data?.Japanese
                        : area === 'EN'
                        ? data[0]?.config?.data?.data?.English
                        : data[0]?.config?.data?.data?.Chinese;
            } else {
                dataConfig = data?.config?.data?.data?.[language];
                attrConfig = data?.config?.data;
                linkConfig =
                    area === 'JP'
                        ? data?.config?.data?.data?.Japanese
                        : area === 'EN'
                        ? data?.config?.data?.data?.English
                        : data?.config?.data?.data?.Chinese;
            }
            item.isfromEC = true;
            item.i18nName = dataConfig?.title;
            item.i18nDesc = dataConfig?.description;
            item.i18nLink1 = linkConfig?.link1;
            item.i18nLink2 = linkConfig?.link2;
            item.tags = {
                ...attrConfig,
                abtestType: attrConfig?.test ? 'ABtesting' : '',
                priority:
                    attrConfig?.priority !== undefined && attrConfig?.priority !== ''
                        ? attrConfig?.priority
                        : 1
            };
            item.updateTime = item.updated;
            return item;
        }) || []
    );
}
